import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Star } from "lucide-react";

const PricingSection = ({ onSelectPlan }) => {
  const plans = [
    {
      name: "Basic",
      price: "$29.99",
      period: "/month",
      description: "Perfect for first-time buyers exploring the market",
      features: [
        "5 property tours per month",
        "Basic background check",
        "Standard credit check",
        "Email support",
        "Mobile app access",
        "Blockchain transaction history",
      ],
      highlighted: false,
    },
    {
      name: "Pro",
      price: "$49.99",
      period: "/month",
      description: "Best for active buyers and renters",
      features: [
        "Unlimited property tours",
        "Enhanced background check",
        "Comprehensive credit report",
        "Priority email & chat support",
        "Mobile app access",
        "Blockchain transaction history",
        "Smart contract templates",
        "Document verification",
      ],
      highlighted: true,
    },
    {
      name: "Enterprise",
      price: "$149.99",
      period: "/month",
      description: "For real estate professionals and investors",
      features: [
        "Unlimited property tours",
        "Premium background check",
        "Real-time credit monitoring",
        "24/7 dedicated support",
        "Mobile app access",
        "Blockchain transaction history",
        "Custom smart contracts",
        "API access",
        "Multi-user accounts",
        "Analytics dashboard",
      ],
      highlighted: false,
    },
  ];

  return (
    <section id="pricing" className="py-24 lg:py-32 bg-white" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C04000] mb-4 block">
            Pricing Plans
          </span>
          <h2 
            className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="pricing-heading"
          >
            Choose Your Subscription
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            All plans include credit check and background verification as prerequisites for property touring. Select the plan that fits your needs.
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative ${
                plan.highlighted
                  ? "border-2 border-[#C04000] shadow-lg pricing-featured"
                  : "border border-slate-200 shadow-sm"
              }`}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
            >
              {plan.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-[#C04000] text-white text-xs font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    Most Popular
                  </span>
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle 
                  className="text-xl font-bold text-slate-900"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {plan.name}
                </CardTitle>
                <CardDescription className="text-slate-500">
                  {plan.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center pb-6">
                <div className="mb-6">
                  <span 
                    className="text-4xl font-bold text-slate-900"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {plan.price}
                  </span>
                  <span className="text-slate-500">{plan.period}</span>
                </div>
                <ul className="space-y-3 text-left">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#C04000] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button
                  className={`w-full ${
                    plan.highlighted
                      ? "bg-[#C04000] hover:bg-[#A03500] text-white"
                      : "bg-white hover:bg-slate-50 text-slate-900 border border-slate-200"
                  }`}
                  onClick={onSelectPlan}
                  data-testid={`select-plan-${plan.name.toLowerCase()}`}
                >
                  Get Started
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Trust Note */}
        <p className="text-center text-sm text-slate-500 mt-10">
          All plans require successful completion of credit and background checks before property touring is enabled.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
